(() => {
  if (customElements.get('hp-legami-card')) return;

  const activeRequests = new Map();
  const connectedCards = new Set();
  let cartObserver;

  function updateCartLabel() {
    const cartLink = document.getElementById('cart-icon-bubble');
    if (!cartLink || !document.querySelector('.hp-legami-shop')) return;
    const labels = Array.from(cartLink.querySelectorAll('.visually-hidden'))
      .map((label) => label.textContent.trim())
      .filter(Boolean);
    const fallback = connectedCards.values().next().value?.dataset.cartLabel;
    if (labels.length || fallback) cartLink.setAttribute('aria-label', labels.join(', ') || fallback);
  }

  function observeCart() {
    if (cartObserver) return;
    const cartLink = document.getElementById('cart-icon-bubble');
    if (!cartLink) return;
    cartObserver = new MutationObserver(updateCartLabel);
    cartObserver.observe(cartLink, { childList: true, subtree: true, characterData: true });
    updateCartLabel();
  }

  customElements.define(
    'hp-legami-card',
    class HpLegamiCard extends HTMLElement {
      connectedCallback() {
        this.productForm = this.querySelector('product-form');
        this.form = this.productForm?.querySelector('form');
        this.select = this.querySelector('[data-hp-variant]');
        this.swatches = this.querySelector('[data-hp-variant-thumbs]');
        this.variantThumbs = Array.from(this.querySelectorAll('[data-hp-variant-thumb]'));
        this.button = this.form?.querySelector('[type="submit"]');
        this.buttonText = this.button?.querySelector('span');
        this.price = this.querySelector('[data-hp-price]');
        this.availability = this.querySelector('[data-hp-availability]');
        this.image = this.querySelector('.hp-legami-card__image');
        this.imageNote = this.querySelector('[data-hp-image-note]');
        this.status = this.querySelector('[data-hp-status]');
        this.cartLink = this.querySelector('[data-hp-cart-link]');
        this.initialAvailable ??= this.dataset.available === 'true';
        if (!this.initialImage && this.image) {
          this.initialImage = Object.fromEntries(
            ['src', 'srcset', 'alt', 'width', 'height'].map((attribute) => [attribute, this.image.getAttribute(attribute)])
          );
        }

        connectedCards.add(this);
        observeCart();
        this.events?.abort();
        this.events = new AbortController();
        const options = { signal: this.events.signal };
        this.select?.addEventListener('change', () => this.updateSelection(), options);
        this.swatches?.addEventListener('click', (event) => this.chooseThumbnail(event), options);
        if (this.swatches && this.select) this.swatches.hidden = false;
        this.addEventListener('submit', (event) => this.prepareSubmit(event), { ...options, capture: true });
        this.addEventListener('submit', (event) => this.lockSubmittedControls(event), options);
        this.addEventListener('shopify:cart:lines-update', (event) => this.observeCartPromise(event), options);
        this.addEventListener('shopify:cart:error', (event) => this.observeCartError(event), options);

        if (this.button && !this.buttonObserver) {
          this.buttonObserver = new MutationObserver(() => this.finishWhenReady());
          this.buttonObserver.observe(this.button, { attributes: true, attributeFilter: ['class'] });
        }
        if (!this.unsubscribeCartUpdate && typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
          this.unsubscribeCartUpdate = subscribe(PUB_SUB_EVENTS.cartUpdate, (event) => {
            const request = this.pending;
            if (
              !request ||
              event.source !== 'product-form' ||
              String(event.productVariantId) !== request.variantId ||
              activeRequests.get(request.variantId) !== request ||
              !event.cartData ||
              event.cartData.status
            ) return;
            request.accepted = true;
            this.finishWhenReady();
          });
        }
        if (!this.pending) this.updateSelection();
      }

      disconnectedCallback() {
        this.events?.abort();
        connectedCards.delete(this);
        if (!connectedCards.size) {
          cartObserver?.disconnect();
          cartObserver = null;
        }
        if (!this.pending) this.disposeObservers();
      }

      disposeObservers() {
        this.buttonObserver?.disconnect();
        this.buttonObserver = null;
        this.unsubscribeCartUpdate?.();
        this.unsubscribeCartUpdate = null;
      }

      updateSelection() {
        if (this.pending) return;
        const option = this.select?.selectedOptions[0];
        const chosen = Boolean(option?.value);
        const available = chosen ? !option.disabled : this.initialAvailable;
        this.price.textContent = chosen ? option.dataset.price : this.dataset.initialPrice;
        this.availability.textContent = available ? this.dataset.availableText : this.dataset.soldOutText;
        this.availability.dataset.available = String(available);
        this.dataset.available = String(available);
        if (this.button) {
          this.button.disabled = !available;
          this.buttonText.textContent = available ? this.dataset.addText : this.dataset.soldOutText;
          this.button.setAttribute('aria-label', available ? this.dataset.addLabel : this.dataset.soldOutLabel);
        }
        if (this.imageNote && this.select) {
          const showImageNote = chosen && option.dataset.hasVariantImage !== 'true';
          this.imageNote.hidden = !showImageNote;
          if (showImageNote) this.select.setAttribute('aria-describedby', this.imageNote.id);
          else this.select.removeAttribute('aria-describedby');
        }
        this.updateThumbnails();
        this.querySelectorAll('[data-hp-product-link]').forEach((link) => {
          link.href = chosen ? option.dataset.url : this.dataset.productUrl;
        });
        if (this.image) {
          const attributes = chosen && option.dataset.src ? option.dataset : this.initialImage;
          ['src', 'srcset', 'alt', 'width', 'height'].forEach((attribute) => {
            const value = attributes?.[attribute];
            if (value) this.image.setAttribute(attribute, value);
            else this.image.removeAttribute(attribute);
          });
          this.image.dataset.src = this.image.getAttribute('src') || '';
          this.image.dataset.srcset = this.image.getAttribute('srcset') || '';
        }
        this.clearFeedback();
      }

      chooseThumbnail(event) {
        const thumbnail = event.target.closest('[data-hp-variant-thumb]');
        if (!thumbnail || !this.swatches.contains(thumbnail) || !this.select || this.select.disabled || this.pending) return;
        const option = Array.from(this.select.options).find((candidate) => candidate.value === thumbnail.value);
        if (!option || option.disabled || thumbnail.disabled) return;
        this.select.value = option.value;
        this.select.dispatchEvent(new Event('change', { bubbles: true }));
      }

      updateThumbnails() {
        if (!this.select) return;
        this.variantThumbs.forEach((thumbnail) => {
          const option = Array.from(this.select.options).find((candidate) => candidate.value === thumbnail.value);
          thumbnail.disabled = Boolean(this.pending) || !option || option.disabled;
          thumbnail.setAttribute('aria-pressed', String(Boolean(this.select.value) && thumbnail.value === this.select.value));
        });
      }

      clearFeedback() {
        if (this.status) this.status.textContent = '';
        if (this.cartLink) this.cartLink.hidden = true;
        this.productForm?.handleErrorMessage?.();
      }

      showError(message) {
        if (this.status) this.status.textContent = '';
        const wrapper = this.querySelector('.product-form__error-message-wrapper');
        const text = wrapper?.querySelector('.product-form__error-message');
        if (wrapper && text) {
          text.textContent = message;
          wrapper.hidden = false;
        }
        if (this.cartLink) this.cartLink.hidden = false;
      }

      prepareSubmit(event) {
        if (event.target !== this.form || typeof this.productForm?.onSubmitHandler !== 'function') return;
        if (this.pending || this.button.disabled || this.button.getAttribute('aria-disabled') === 'true') {
          event.preventDefault();
          event.stopImmediatePropagation();
          return;
        }
        if (!this.form.checkValidity()) {
          event.preventDefault();
          event.stopImmediatePropagation();
          this.form.reportValidity();
          return;
        }
        const variantId = this.form.querySelector('[name="id"]')?.value;
        if (!variantId) {
          event.preventDefault();
          event.stopImmediatePropagation();
          return;
        }
        if (activeRequests.has(variantId)) {
          event.preventDefault();
          event.stopImmediatePropagation();
          this.showError(this.dataset.alreadyAdding);
          return;
        }
        this.clearFeedback();
        this.pending = { variantId, accepted: false, started: false, hasPromise: false, error: '' };
        activeRequests.set(variantId, this.pending);
        this.setAttribute('aria-busy', 'true');
        this.status.textContent = this.dataset.addingText;
      }

      lockSubmittedControls(event) {
        if (event.target !== this.form || !this.pending) return;
        if (!this.button.classList.contains('loading')) {
          this.releaseRequest();
          return;
        }
        this.pending.started = true;
        this.button.disabled = true;
        if (this.select) this.select.disabled = true;
        this.updateThumbnails();
      }

      observeCartPromise(event) {
        if (event.target !== this.productForm || !this.pending || !event.promise?.then) return;
        const request = this.pending;
        request.hasPromise = true;
        event.promise.then(
          (result) => {
            if (this.pending !== request) return;
            if (result?.cart) request.accepted = true;
            else request.error = this.dataset.networkError;
            request.hasPromise = false;
            this.finishWhenReady();
          },
          () => {
            if (this.pending !== request) return;
            if (!request.accepted && !request.error) request.error = this.dataset.networkError;
            request.hasPromise = false;
            this.finishWhenReady();
          }
        );
      }

      observeCartError(event) {
        if (event.target !== this.productForm || !this.pending || this.pending.accepted) return;
        this.pending.error = event.code === 'INVALID' && typeof event.error === 'string'
          ? event.error
          : this.dataset.networkError;
        this.showError(this.pending.error);
        this.finishWhenReady();
      }

      finishWhenReady() {
        const request = this.pending;
        if (!request?.started || this.button.classList.contains('loading')) return;
        if (!request.accepted && !request.error && request.hasPromise) return;
        if (request.accepted) {
          this.productForm.handleErrorMessage?.();
          this.status.textContent = this.dataset.addedText;
          this.cartLink.hidden = false;
          updateCartLabel();
        } else {
          const existingError = this.querySelector('.product-form__error-message-wrapper:not([hidden]) .product-form__error-message');
          this.showError(existingError?.textContent.trim() || request.error || this.dataset.networkError);
        }
        this.releaseRequest();
      }

      releaseRequest() {
        const request = this.pending;
        if (request && activeRequests.get(request.variantId) === request) activeRequests.delete(request.variantId);
        this.pending = null;
        this.removeAttribute('aria-busy');
        if (this.select) this.select.disabled = false;
        if (this.button) this.button.disabled = this.dataset.available !== 'true';
        this.updateThumbnails();
        if (!this.isConnected) this.disposeObservers();
      }
    }
  );
})();
